import { findByArtworkGroupAPI } from '@/service/resource/resource'
import { formatResourceData } from '@/utils/common'
import type { ResourceMarkType } from '@/constants/resource-marks'

/**
 * 资源数据接口定义
 */
export interface ResourceItem {
  id: string
  file_id: string
  title: string
  type: string
  url: string
  endpoint: string
  apply_id: string
  endpoint_type: string
  [key: string]: any
}

/**
 * 统一的资源获取方法
 * @param mark 资源标识
 * @returns 格式化后的资源数据
 */
export const getResourceByMark = async (mark: ResourceMarkType): Promise<ResourceItem[]> => {
  try {
    const res = await findByArtworkGroupAPI(mark)
    if (res.code === 200) {
      return formatResourceData(res.data)
    } else {
      console.error(`获取资源失败: ${res.message}`)
      return []
    }
  } catch (error) {
    console.error('获取资源失败:', error)
    return []
  }
}

/**
 * 批量获取多个资源
 * @param marks 资源标识数组
 * @returns Promise 数组，每个 Promise 对应一个资源的数据
 */
export const getMultipleResources = async (
  marks: ResourceMarkType[],
): Promise<ResourceItem[][]> => {
  try {
    const promises = marks.map((mark) => getResourceByMark(mark))
    return await Promise.all(promises)
  } catch (error) {
    console.error('批量获取资源失败:', error)
    return marks.map(() => [])
  }
}

/**
 * 获取资源并返回对象形式
 * @param markMap 资源标识映射对象 { key: mark }
 * @returns 以 key 为键的资源数据对象
 */
export const getResourcesAsObject = async <T extends Record<string, ResourceMarkType>>(
  markMap: T,
): Promise<Record<keyof T, ResourceItem[]>> => {
  try {
    const keys = Object.keys(markMap) as (keyof T)[]
    const marks = Object.values(markMap)
    const results = await getMultipleResources(marks)

    const resourceObject = {} as Record<keyof T, ResourceItem[]>
    keys.forEach((key, index) => {
      resourceObject[key] = results[index] || []
    })

    return resourceObject
  } catch (error) {
    console.error('获取资源对象失败:', error)
    const emptyResult = {} as Record<keyof T, ResourceItem[]>
    Object.keys(markMap).forEach((key) => {
      emptyResult[key as keyof T] = []
    })
    return emptyResult
  }
}

/**
 * 从资源数据中查找特定 code 的项目
 * @param resources 资源数据数组
 * @param code 要查找的 code
 * @returns 找到的资源项或 undefined
 */
export const findResourceByCode = (
  resources: ResourceItem[],
  code: string,
): ResourceItem | undefined => {
  return resources.find((item) => item.code === code)
}

/**
 * 获取资源的URL地址
 * @param mark 资源标识
 * @param code 资源代码（可选）
 * @returns 资源URL
 */
export async function getResourceUrl(mark: ResourceMarkType, code?: string): Promise<string> {
  const resource = await getResourceByMark(mark)

  if (code) {
    const targetResource = resource.find((item) => item.code === code)
    return targetResource?.file_full_url || ''
  }

  return resource[0]?.file_full_url || ''
}
